import { FC, ReactElement, useEffect, useRef, useState } from "react";
import { fetchAsync, postAsync, postWithoutSign } from "../services/ApiHandler";
import toast, { Toaster } from "react-hot-toast";
import { CardRegistrationDialog, CardRegistrationDialogProps } from "../parts/CardRegisterDialog";
import { GenerationCreditDialog, GenerationCreditDialogProps } from "../parts/GenerationCreditDialog";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useHeaderStore } from "../parts/CommonHeader";
import { getNextPlanId, getPlanDescription } from "../services/SubscriptionService";
import { getPlanSummary } from "../services/SubscriptionService";
import { SubscriptionDialog } from "../parts/SubscriptionDialog";
import { SubscriptionDialogProps } from "../parts/SubscriptionDialog";
import { LoginDialog } from "../parts/LoginDialog";
import { Helmet } from "react-helmet";
import { getModalOffset } from "../services/ScreenUtil";
import { StripeProduct } from "../model/StripeProduct";

export const PlanPage: FC = () => {
  const [span, setSpan] = useState("month");
  const [cardProps, setCardProps] = useState<CardRegistrationDialogProps | undefined>(undefined);
  const [generationCreditDialogProps, setGenerationCreditDialogProps] = useState<GenerationCreditDialogProps | undefined>(undefined);
  const [subscriptionDialogProps, setSubscriptionDialogProps] = useState<SubscriptionDialogProps | undefined>(undefined);
  const [creditDialogOpened, { open, close }] = useDisclosure(false);
  const [subscriptionDialogOpened, { open: openSubscriptionDialog, close: closeSubscriptionDialog }] = useDisclosure(false);
  const { subscription, setSubscription, card, fetchCard, isLoggedIn, setIsLoggedIn, screenWidth } = useHeaderStore();
  const [loginDialogOpened, { open: openLoginDialog, close: closeLoginDialog }] = useDisclosure(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    fetchStripeProducts();
  }, []);
  
  var planTextStyleMap: { [key: string]: any; } = {
    'free': '#868E96',
    'lite': '#03034F',
    'standard': '#03034F',
    'advanced': 'linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)',
  };

  var planRankMap: { [key: string]: any; } = {
    'free': 0,
    'lite': 1,
    'standard': 2,
    'advanced': 3,
  };

  async function fetchStripeProducts() {
    const list = await fetchAsync<StripeProduct[]>('Product/GetProductList');
    setStripeProducts(list.filter(product => product.productType === 'credit').sort((a, b) => a.price - b.price));
  }
  
  function buildSpanSwitch(){
    return <div className="flex justify-center mt-4 bg-gray-200 w-[168px] mx-auto rounded-lg">
      <span onClick={() => setSpan('month')} className={`my-1 rounded-md cursor-pointer w-[80px] ${span === 'month' ? 'bg-white' : ''}` }>月</span>
      <span onClick={() => setSpan('year')} className={`my-1 rounded-md cursor-pointer w-[80px] ${span === 'year' ? 'bg-white' : ''}`}>年</span>
    </div>
  }

  function onClickCreditPurchase(){
    open();
    setGenerationCreditDialogProps({
      onPurchase: () => {
        toast.success("購入しました");
        setGenerationCreditDialogProps(undefined);
      }
    } as GenerationCreditDialogProps);
  }

  function onClickPlanChange(planId: string){
    setSubscriptionDialogProps({
      planId: planId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  function buildPriceArea(monthlyPrice: number, yearlyPrice: number, planId: string, planName: string){
    var textStyle = planTextStyleMap[planId];
    var rank = planRankMap[planId];
    var currentRank = -1;
    if(subscription !== undefined) {
      currentRank = planRankMap[subscription.internalProductId.split('_')[0]];
    }

    return <>
      <div className="max-w-[320px] mx-auto sm:mx-2 text-left py-4 px-6 rounded-lg bg-[#F8F9FA] mb-6 sm:mb-0">
        <div className="h-[260px]">
          <div className="text-lg font-bold">
            <span style={{background: textStyle, color: "transparent", backgroundClip: "text"}}>{planName}</span>
          </div>
          { span === 'month' &&
            <div className="text-2xl font-bold">
              <span>{`¥ ${monthlyPrice.toLocaleString('ja-JP')}`}</span>
              { planId !== 'free' &&
                <span className="text-sm text-[#868E96]"> / 月</span>
              }
            </div>
          }
          { span === 'year' &&
            <div className="text-2xl font-bold">
              <span>{`¥ ${yearlyPrice.toLocaleString('ja-JP')}`}</span>
              { planId !== 'free' &&
                <span className="text-sm text-[#868E96]"> / 年</span>
              }
            </div>
          }
          <div className="mt-2 font-bold text-sm">{getPlanSummary(planId)}</div>
          <div className="mt-4 whitespace-pre-wrap text-sm">{getPlanDescription(planId)}</div>
        </div>
        { subscription === undefined &&
          <div className="h-16"></div>
        }
        { ( subscription !== undefined && (isLoggedIn === false || !(planId === 'free' && subscription?.internalProductId !== 'free'))) &&
        <div className="flex justify-center mt-4 mb-2">
          { isLoggedIn === false &&
            <div onClick={() => openLoginDialog()} className="clickable bg-[#EAEAFF] text-[#0909FF] px-4 py-2 rounded-md w-full text-center font-bold">アカウント登録</div>
          }
          { isLoggedIn === true && subscription?.internalProductId.startsWith(planId) &&
            <div className="bg-[#EAEAFF] text-[#0909FF] px-4 py-2 rounded-md w-full text-center font-bold">現在ご利用中のプラン</div>
          }
          { (isLoggedIn === true && rank > currentRank) &&
            <button onClick={() => onClickPlanChange(planId)} className="bg-[#0909FF] text-white px-4 py-2 rounded-md w-full font-bold">このプランを選ぶ</button>
          }
        </div>
        }
      </div>
    </>
  }

  function onLogin(){
    closeLoginDialog();
    fetchCard();
    setIsLoggedIn(true);
  }

  function onClickLogin(){
    setIsLoginMode(true);
    openLoginDialog();
  }

  function onClickRegisterLink(){
    setIsLoginMode(false);
    openLoginDialog();
  }

  function onClickUpgradePlan(){
    close();
    const nextPlanId = getNextPlanId(subscription);
    setSubscriptionDialogProps({
      planId: nextPlanId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentPlanIndex < 3) {
      setCurrentPlanIndex(currentPlanIndex + 1);
    }
    if (isRightSwipe && currentPlanIndex > 0) {
      setCurrentPlanIndex(currentPlanIndex - 1);
    }
  };

  return <>
    <Helmet>
      <title>料金プラン - Drop AI</title>
      <meta name="description" content="Drop AI の料金プランについて確認できます。" />

      <meta property="og:url" content="https://drop4i.com/plan" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="料金プラン - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="Drop AI の料金プランについて確認できます。" />
    </Helmet>
    <Toaster />
    <Modal size="auto" opened={loginDialogOpened} onClose={closeLoginDialog} yOffset={getModalOffset(screenWidth)}>
      <LoginDialog onCancel={() => closeLoginDialog()} onLogin={() => onLogin()} isLogin={isLoginMode} />
    </Modal>
    { cardProps !== undefined &&
    <CardRegistrationDialog clientSecret={cardProps?.clientSecret!} onClose={() => setCardProps(undefined)} />
    }
    { generationCreditDialogProps !== undefined &&
      <Modal padding={0} size="auto" opened={creditDialogOpened} onClose={close} yOffset={getModalOffset(screenWidth)} closeButtonProps={{ className: 'mr-3' }}>
        <GenerationCreditDialog
          onPurchase={() => setGenerationCreditDialogProps(undefined)}
          onCancel={() => setGenerationCreditDialogProps(undefined)}
          onUpgrade={() => onClickUpgradePlan()}
          />
      </Modal>
    }
    { subscriptionDialogProps !== undefined &&
      <Modal size="auto" opened={subscriptionDialogOpened} onClose={closeSubscriptionDialog} yOffset={getModalOffset(screenWidth)}>
        <SubscriptionDialog onPurchase={() => setSubscriptionDialogProps(undefined)} onCancel={() => setSubscriptionDialogProps(undefined)} planId={subscriptionDialogProps?.planId} />
      </Modal>
    }
    <div className="text-center mt-12">
      <div className="text-2xl font-bold">料金プラン</div>
      { buildSpanSwitch() }
      <div className="block sm:flex justify-center mt-12">
        { buildPriceArea(0, 0, "free", "Freeプラン") }
        { buildPriceArea(980, 10000, "lite", "Liteプラン") }
        { buildPriceArea(3900, 38000, "standard", "Standardプラン") }
        { buildPriceArea(11000, 105000, "advanced", "Advancedプラン") }
      </div>
      <div className="text-sm mt-8">
        <span>※ 料金プラン変更の詳細については</span>
        <a href="/article/help/about-plan" className="underline text-[#0909FF]">ヘルプページ</a>
        <span>をご確認ください。</span>
      </div>
      <div className="mt-20 text-2xl font-bold">プランの比較</div>
      { buildSpanSwitch() }
      <table className="w-[1000px] mx-auto mt-12 mb-6 hidden sm:table">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="h-12"></th>
            <th className="text-gray-500">Freeプラン</th>
            <th>Liteプラン</th>
            <th>Standardプラン</th>
            <th className="gradation-text">Advancedプラン</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-200">
            <td>価格（税込)</td>
            <td className="font-bold">¥0</td>
            <td className="py-6 font-bold">
              { span === 'year' ? <div>年間 ¥10,000</div> : <div>¥980</div> }
              { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥833)</div> }
            </td>
            <td className="font-bold">
              { span === 'year' ? <div>年間 ¥38,000</div> : <div>¥3,900</div> }
              { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥3,166)</div> }
            </td>
            <td className="font-bold">
              { span === 'year' ? <div>年間 ¥105,000</div> : <div>¥11,000</div> }
              { span === 'year' && <div className="text-sm text-[#868E96] font-normal">(月あたり ¥8,750)</div> }
            </td>
        </tr>
          <tr className="border-b border-gray-200">
            <td className="py-6">1日あたり付与クレジット数</td>
            <td className="font-bold">30</td>
            <td className="text-[#0909FF] font-bold">-</td>
            <td className="text-[#0909FF] font-bold">-</td>
            <td className="text-[#0909FF] font-bold"></td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-6">1ヶ月あたり付与クレジット数</td>
            <td className="font-bold">-</td>
            <td className="text-[#0909FF] font-bold">1,800</td>
            <td className="text-[#0909FF] font-bold">7,500</td>
            <td className="text-[#0909FF] font-bold">26,000</td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-4">
              <div>広告非表示</div>
              <div className="text-xs">※1</div>
            </td>
            <td><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-6">同時画像生成</td>
            <td className="font-bold">1回まで</td>
            <td className="font-bold">4回まで</td>
            <td className="font-bold">4回まで</td>
            <td className="font-bold">4回まで</td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-4">
              <div>生成画像（画像）の保存期限</div>
              <div className="text-xs">※2</div>
            </td>
            <td className="font-bold">30日間</td>
            <td className="font-bold">90日間</td>
            <td className="text-[#0909FF] font-bold">180日間</td>
            <td className="text-[#0909FF] font-bold">無制限</td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-4">
              <div>画像の詳細設定</div>
              <div className="text-xs">※3</div>
            </td>
            <td><img src="/images/icon-close.svg" alt="x" className="h-[16px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
            <td><img src="/images/icon/icon-circle.svg" alt="x" className="h-[24px] mx-auto" /></td>
          </tr>
          <tr>
            <td className="py-4">
              <div className="grid items-center" style={{ gridTemplateRows: "32px repeat(8, 48px)" }}>
                <div className="font-bold">利用可能なAIモデル</div>
                <div className="text-xs text-gray-500 font-bold">画像生成AIモデル</div>
                <div className="text-sm text-gray-600">Stable Diffusion XL</div>
                <div className="text-sm text-gray-600">Nano Banana</div>
                <div className="text-sm text-gray-600">Nano Banana Pro</div>
                <div className="text-xs text-gray-500 font-bold">動画生成AIモデル</div>
                <div className="text-sm text-gray-600">Veo 3.1 Lite</div>
                <div className="text-sm text-gray-600">Veo 3.1 Fast</div>
                <div className="text-sm text-gray-600">Veo 3.1 Standard</div>
              </div>
            </td>
            <td>
              <div className="grid items-center" style={{ gridTemplateRows: "32px repeat(8, 48px)" }}>
                <div></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon-close.svg" alt="x" className="h-[16px]" /></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon-close.svg" alt="x" className="h-[16px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon-close.svg" alt="x" className="h-[16px]" /></div>
              </div>
            </td>
            <td>
              <div className="grid items-center" style={{ gridTemplateRows: "32px repeat(8, 48px)" }}>
                <div></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon-close.svg" alt="x" className="h-[16px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon-close.svg" alt="x" className="h-[16px]" /></div>
              </div>
            </td>
            <td>
              <div className="grid items-center" style={{ gridTemplateRows: "32px repeat(8, 48px)" }}>
                <div></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex flex-col items-center justify-center">
                  <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                  <div className="text-[11px] text-[#03034F] font-bold leading-none mt-1">4K動画生成可能</div>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                  <div className="text-[11px] text-[#03034F] font-bold leading-none mt-1 opacity-0 select-none">4K動画生成可能</div>
                </div>
              </div>
            </td>
            <td>
              <div className="grid items-center" style={{ gridTemplateRows: "32px repeat(8, 48px)" }}>
                <div></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div></div>
                <div className="flex items-center justify-center"><img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" /></div>
                <div className="flex flex-col items-center justify-center">
                  <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                  <div className="text-[11px] text-[#03034F] font-bold leading-none mt-1">4K動画生成可能</div>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                  <div className="text-[11px] text-[#03034F] font-bold leading-none mt-1">4K動画生成可能</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="block sm:hidden px-4 mt-10">
        {/* Plan carousel for mobile */}
        <div className="relative">
          {/* Plan selector buttons */}
          <div className="flex justify-center mb-6 gap-2">
            <button 
              onClick={() => setCurrentPlanIndex(0)}
              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                currentPlanIndex === 0 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              Free
            </button>
            <button 
              onClick={() => setCurrentPlanIndex(1)}
              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                currentPlanIndex === 1 ? 'bg-[#03034F] text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              Lite
            </button>
            <button 
              onClick={() => setCurrentPlanIndex(2)}
              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                currentPlanIndex === 2 ? 'bg-[#03034F] text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              Standard
            </button>
            <button 
              onClick={() => setCurrentPlanIndex(3)}
              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                currentPlanIndex === 3 ? 'bg-gradient-to-r from-[#0000F3] to-[#F416BF] text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              Advanced
            </button>
          </div>

          {/* Plan details carousel */}
          <div 
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentPlanIndex * 100}%)` }}
            >
              {/* Free Plan */}
              <div className="w-full flex-shrink-0 px-2">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-500 mb-4">Freeプラン</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600">価格（税込)</div>
                      <div className="font-bold text-lg">¥0</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">1日に付与されるクレジット数</div>
                      <div className="font-bold">30</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">広告非表示 ※1</div>
                      <div className="flex justify-center">
                        <img src="/images/icon-close.svg" alt="x" className="h-[16px]" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">同時画像生成</div>
                      <div className="font-bold">1回まで</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">生成画像（画像）の保存期限 ※2</div>
                      <div className="font-bold">30日間</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">画像の詳細設定 ※3</div>
                      <div className="flex justify-center">
                        <img src="/images/icon-close.svg" alt="x" className="h-[16px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lite Plan */}
              <div className="w-full flex-shrink-0 px-2">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-[#03034F] mb-4">Liteプラン</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600">価格（税込)</div>
                      <div className="font-bold text-lg">
                        { span === 'year' ? <div>年間 ¥10,000<div className="text-sm text-[#868E96] font-normal">(月あたり ¥833)</div></div> : <div>¥980</div> }
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">1ヶ月あたり付与クレジット</div>
                      <div className="font-bold text-[#0909FF]">1800</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">広告非表示 ※1</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">同時画像生成</div>
                      <div className="font-bold">4回まで</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">生成画像（画像）の保存期限 ※2</div>
                      <div className="font-bold">90日間</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">画像の詳細設定 ※3</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Standard Plan */}
              <div className="w-full flex-shrink-0 px-2">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-[#03034F] mb-4">Standardプラン</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600">価格（税込)</div>
                      <div className="font-bold text-lg">
                        { span === 'year' ? <div>年間 ¥38,000<div className="text-sm text-[#868E96] font-normal">(月あたり ¥3,166)</div></div> : <div>¥3,900</div> }
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">1ヶ月あたり付与クレジット</div>
                      <div className="font-bold text-[#0909FF]">7,500</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">広告非表示 ※1</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">同時画像生成</div>
                      <div className="font-bold">4回まで</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">生成画像（画像）の保存期限 ※2</div>
                      <div className="font-bold text-[#0909FF]">180日間</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">画像の詳細設定 ※3</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Plan */}
              <div className="w-full flex-shrink-0 px-2">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold gradation-text mb-4">Advancedプラン</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600">価格（税込)</div>
                      <div className="font-bold text-lg">
                        { span === 'year' ? <div>年間 ¥105,000<div className="text-sm text-[#868E96] font-normal">(月あたり ¥8,750)</div></div> : <div>¥11,000</div> }
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">1ヶ月あたり付与クレジット</div>
                      <div className="font-bold text-[#0909FF]">26,000</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">広告非表示 ※1</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">同時画像生成</div>
                      <div className="font-bold">4回まで</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">生成画像（画像）の保存期限 ※2</div>
                      <div className="font-bold text-[#0909FF]">無制限</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">画像の詳細設定 ※3</div>
                      <div className="flex justify-center">
                        <img src="/images/icon/icon-circle.svg" alt="o" className="h-[24px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-sm text-[#868E96] mt-12 mx-8 text-left sm:text-center">
        <div>※1 生成時の広告表示のON / OFF をコン トロールできます。マイページで設定可能です。</div>
        <div>※2 保存期限を経過した画像は閲覧履歴から削除されます。</div>
        <div>※3 シード値、ステップ数、サンプリングステップ数を調整できます。</div>
      </div>

      <div className="mt-12 px-4">
        <div className="text-lg font-bold text-center">画像1生成あたりの消費クレジット数</div>
        <div className="mt-6 max-w-[1000px] mx-auto overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="h-12"></th>
                <th className="text-gray-500">Freeプラン</th>
                <th>Liteプラン</th>
                <th>Standardプラン</th>
                <th className="gradation-text">Advancedプラン</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-6">Stable Diffusion XL</td>
                <td className="font-bold">5</td>
                <td className="font-bold">3</td>
                <td className="font-bold">3</td>
                <td className="font-bold">2</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana</td>
                <td className="font-bold">10</td>
                <td className="font-bold">10</td>
                <td className="font-bold">9</td>
                <td className="font-bold">8</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana Pro（1K/2K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">38</td>
                <td className="font-bold">36</td>
                <td className="font-bold">34</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana Pro（4K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">58</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-12 px-4">
        <div className="text-lg font-bold text-center">動画1秒あたりの消費クレジット数</div>
        <div className="mt-6 max-w-[1000px] mx-auto overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="h-12"></th>
                <th className="text-gray-500">Freeプラン</th>
                <th>Liteプラン</th>
                <th>Standardプラン</th>
                <th className="gradation-text">Advancedプラン</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-6">Veo 3.1 Lite（720p, 1080p）</td>
                <td className="font-bold">44</td>
                <td className="font-bold">41</td>
                <td className="font-bold">39</td>
                <td className="font-bold">38</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Veo 3.1 Fast（720p, 1080p）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">77</td>
                <td className="font-bold">71</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Veo 3.1 Fast（4K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">177</td>
                <td className="font-bold">164</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Veo 3.1 Standard（720p, 1080p）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">193</td>
                <td className="font-bold">190</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Veo 3.1 Standard（4K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">299</td>
                <td className="font-bold">284</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="text-sm text-[#868E96] mt-2 mx-auto max-w-[1000px] text-left sm:text-center">
          ※ 動画は1秒あたりの消費量です（8秒生成の場合は8倍消費）
        </div>
      </div>

      <div className="mt-12 px-4">
        <div className="text-lg font-bold text-center">プランごとの各AIモデルの1ヶ月あたりの生成回数目安（画像）※</div>
        <div className="mt-6 max-w-[1000px] mx-auto overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="h-12"></th>
                <th className="text-gray-500">Freeプラン</th>
                <th>Liteプラン</th>
                <th>Standardプラン</th>
                <th className="gradation-text">Advancedプラン</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-6">Stable Diffusion XL</td>
                <td className="font-bold">180</td>
                <td className="font-bold">600</td>
                <td className="font-bold">2,500</td>
                <td className="font-bold">13,000</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana</td>
                <td className="font-bold">90</td>
                <td className="font-bold">180</td>
                <td className="font-bold">833</td>
                <td className="font-bold">3,250</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana Pro（1K/2K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">47</td>
                <td className="font-bold">208</td>
                <td className="font-bold">765</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-6">Nano Banana Pro（4K）</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">-</td>
                <td className="font-bold">448</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="text-sm text-[#868E96] mt-2 mx-auto max-w-[1000px] text-left sm:text-center">
          ※ 全て同じモデルでクレジットを消費した場合の想定作成可能数
        </div>
      </div>

      <div className="mt-16 text-2xl font-bold text-primary">追加のクレジット購入</div>
      <div className="mt-4 mx-8 text-gray-500">足りなくなったクレジット数を追加購入できます。</div>
      <table className="w-[300px] sm:w-[800px] mx-auto mt-12 mb-12">
        <thead>
          <tr>
            <th>付与クレジット数</th>
            <th>価格（税込）</th>
          </tr>
        </thead>
        <tbody>
          { stripeProducts.map((product) => (
            <tr className="border-b border-gray-200">
              <td className="py-4">{product.point}</td>
              <td className="font-bold">¥{product.price.toLocaleString('ja-JP')}</td>
            </tr>
          ))
          }
        </tbody>
      </table>
      {isLoggedIn === true &&
      <div className="flex justify-center mt-4 mb-20">
        <button onClick={onClickCreditPurchase} className="bg-[#0909FF] text-white px-4 py-2 rounded-md w-[300px] font-bold">追加クレジット購入に進む</button>
      </div>
      }
      {isLoggedIn === false &&
      <>
      <div className="flex justify-center mt-4">
        <button onClick={onClickLogin} className="gradation-button text-white px-4 py-2 rounded-md w-[300px]">ログイン</button>
      </div>
      <div className="flex justify-center mt-6 mb-20">
        <a onClick={onClickRegisterLink} className="underline cursor-pointer text-[#0909FF]">アカウントをお持ちではない方はこちら</a>
      </div>
      </>
      }
    </div>
  </>;
}
